// Shared, page-agnostic Location/System management actions.
//
// These are the ABILITIES that used to live only inside LocationsPage / SystemsPage as
// component-local handlers. They are extracted here verbatim (same endpoints, same payloads,
// same unit conversions) so the in-viewer Browser pane can offer the identical capability
// without embedding those page components. Nothing here touches React or the viewer — every
// function is a thin, typed wrapper over apiService plus the pure CSV/tree helpers.
//
// Canonical single-source rule: if a Location/System mutation needs to happen from more than
// one surface, it belongs in THIS module — not copy-pasted into another component.

import { apiService } from './ApiService';
import { convertApiPoiToComponent } from '../utils/POIConverter';
import { parseCustomData } from '../utils/POIFieldExtractor';

// ── Types ────────────────────────────────────────────────────────────────────

/** Minimal shape of a row from GET /assets/:id/locations(/tree). */
export interface LocationRow {
  id: string;
  name: string;
  type?: string;
  parent_id?: string | null;
  is_custom?: boolean;
  description?: string;
  source_file?: string;
  elevation?: number | string | null;
  height?: number | string | null;
  geometry_polygon?: any;
  properties?: any;
  position?: { x?: number; y?: number; z?: number };
  position_x?: number | string;
  position_y?: number | string;
  position_z?: number | string;
  children?: LocationRow[];
  [key: string]: any;
}

export interface ReassignResult {
  updated?: number;
  unchanged?: number;
  failed?: number;
  total?: number;
  [key: string]: any;
}

export interface GeolocationStats {
  assigned?: number;
  multipleSpaces?: number;
  storeyFallback?: number;
  unassigned?: number;
  [key: string]: any;
}

export interface RoomPolygon {
  points: { x: number; y: number }[];
  minZ: number;
  maxZ: number;
  sourceFile: string;
  dbId: number;
}

/** Viewer-supplied room-polygon extractor (AutodeskViewer.extractAllRoomPolygons). */
export type ExtractRoomPolygonsFn = () => Promise<Map<string, RoomPolygon>>;

// ── Reads ────────────────────────────────────────────────────────────────────

/** Full location tree (same endpoint LocationsPage renders from — nested via `children`). */
export const fetchLocationTree = async (assetId: string): Promise<LocationRow[]> => {
  const res = await apiService.get(`/assets/${assetId}/locations/tree`);
  return Array.isArray(res) ? res : (res?.locations || []);
};

/** Flatten a location tree (or an already-flat list) into a single array. */
export const flattenLocations = (nodes: LocationRow[]): LocationRow[] => {
  const out: LocationRow[] = [];
  const walk = (list: LocationRow[]) => {
    for (const node of list || []) {
      out.push(node);
      if (node.children && node.children.length > 0) walk(node.children);
    }
  };
  walk(nodes || []);
  return out;
};

/** Properties available for location (storey/space) reassignment. */
export const fetchAvailableLocationProperties = async (assetId: string): Promise<string[]> => {
  const data = await apiService.get(`/assets/${assetId}/pois/available-properties`);
  return data?.properties || [];
};

/** Properties available for system reassignment (plus their distinct values). */
export const fetchAvailableSystemProperties = async (
  assetId: string,
): Promise<{ properties: string[]; propertyValues: Record<string, any> }> => {
  const data = await apiService.get(`/assets/${assetId}/pois/available-system-properties`);
  return { properties: data?.properties || [], propertyValues: data?.propertyValues || {} };
};

/**
 * POIs of an asset in the component shape LocationsPage uses for its "assign existing asset"
 * picker — the extra passthrough fields are what its assign/remove payloads rely on.
 */
export const fetchAssignablePOIs = async (assetId: string): Promise<any[]> => {
  const rawPOIs = await apiService.get(`/assets/${assetId}/pois`);
  const list = Array.isArray(rawPOIs) ? rawPOIs : [];
  return list.map((apiPoi: any) => ({
    ...convertApiPoiToComponent(apiPoi),
    title: apiPoi.title,
    subcategory: apiPoi.subcategory,
    poi_type_name: apiPoi.poi_type_name,
    custom_data: apiPoi.custom_data,
    location_id: apiPoi.location_id,
  }));
};

// ── Rooms CSV export (verbatim from LocationsPage) ───────────────────────────

const escapeCSV = (val: string): string => {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
};

/** Recursively collect all rooms (type === 'Spaces') with their parent storey name. */
export const collectRooms = (locs: LocationRow[], parentStoreyName: string = ''): any[] => {
  const rooms: any[] = [];
  for (const loc of locs || []) {
    if (loc.type === 'Spaces') {
      rooms.push({ ...loc, storeyName: parentStoreyName });
    }
    if (loc.children) {
      const nextParent = loc.type === 'Building Storey' ? loc.name : parentStoreyName;
      rooms.push(...collectRooms(loc.children, nextParent));
    }
  }
  return rooms;
};

/**
 * Build the rooms CSV text. Returns null when there is nothing to export.
 * Unit conversions preserved exactly: polygon coords are in feet, Revit height is in cm.
 */
export const buildRoomsCSV = (locations: LocationRow[]): string | null => {
  const rooms = collectRooms(locations);
  if (rooms.length === 0) return null;

  const headers = [
    'Name', 'Description', 'Storey', 'Source File', 'Area (m²)', 'Volume (m³)',
    'Elevation (m)', 'Height (m)',
  ];

  const FT2_TO_M2 = 0.092903;
  const FT_TO_M = 0.3048;

  const rows = rooms.map(room => {
    // Parse geometry_polygon for area (stored in ft², convert to m²)
    let areaFt2 = NaN;
    const geom = room.geometry_polygon;
    if (geom) {
      try {
        const parsed = typeof geom === 'string' ? JSON.parse(geom) : geom;
        if (parsed?.area) areaFt2 = parseFloat(parsed.area);
      } catch { /* malformed geometry — fall through to properties.area */ }
    }
    // Fallback to properties.area
    if (isNaN(areaFt2)) {
      const props = room.properties || {};
      try {
        const parsed = typeof props === 'string' ? JSON.parse(props) : props;
        if (parsed?.area) areaFt2 = parseFloat(parsed.area);
      } catch { /* malformed properties — leave area blank */ }
    }
    const areaM2 = !isNaN(areaFt2) ? areaFt2 * FT2_TO_M2 : NaN;
    const areaStr = !isNaN(areaM2) ? String(Math.round(areaM2 * 100) / 100) : '';

    // Height: values > 10 are cm (from Revit), ≤ 10 are already meters (default/IFC)
    const rawHeight = room.height != null ? parseFloat(String(room.height)) : NaN;
    const heightM = !isNaN(rawHeight) ? (rawHeight > 10 ? rawHeight / 100 : rawHeight) : NaN;
    const heightStr = !isNaN(heightM) ? String(Math.round(heightM * 100) / 100) : '';

    // Elevation: stored in feet, convert to meters
    const rawElev = room.elevation != null ? parseFloat(String(room.elevation)) : NaN;
    const elevM = !isNaN(rawElev) ? rawElev * FT_TO_M : NaN;
    const elevStr = !isNaN(elevM) ? String(Math.round(elevM * 100) / 100) : '';

    // Volume = area(m²) × height(m) = m³
    const volume = (!isNaN(areaM2) && !isNaN(heightM) && areaM2 > 0 && heightM > 0)
      ? String(Math.round(areaM2 * heightM * 100) / 100)
      : '';

    return [
      room.name || '',
      room.description || '',
      room.storeyName || '',
      room.source_file || '',
      areaStr,
      volume,
      elevStr,
      heightStr,
    ].map(v => escapeCSV(v));
  });

  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
};

/** Build + download the rooms CSV. No-op when there are no rooms. */
export const exportRoomsCSV = (locations: LocationRow[]): boolean => {
  const csv = buildRoomsCSV(locations);
  if (!csv) return false;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `rooms-export-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
  return true;
};

// ── Bulk assignment ──────────────────────────────────────────────────────────

/** Re-derive storey/space assignment for every POI from the chosen element properties. */
export const reassignLocations = async (
  assetId: string,
  opts: { storeyProperty?: string; spaceProperty?: string },
): Promise<ReassignResult> =>
  apiService.post(`/assets/${assetId}/pois/reassign-locations`, {
    storeyProperty: opts.storeyProperty || undefined,
    spaceProperty: opts.spaceProperty || undefined,
  });

/** Re-derive system category/subcategory levels for every POI. */
export const reassignSystems = async (
  assetId: string,
  opts: { systemProperty: string; subcategoryLevels: string[] },
): Promise<ReassignResult> =>
  apiService.post(`/assets/${assetId}/pois/reassign-systems`, {
    systemProperty: opts.systemProperty,
    subcategoryLevels: (opts.subcategoryLevels || []).filter(level => level !== ''),
  });

/**
 * Geolocation assignment — the full three-step flow from LocationsPage:
 *   1. ask the server which spaces are missing polygons (unavailable → skip straight to 3),
 *   2. extract the missing polygons from the viewer and cache them server-side,
 *   3. run the assignment against the cached polygons.
 * `extractPolygons` is optional: without a viewer the cached polygons are used as-is.
 */
export const assignByGeolocation = async (
  assetId: string,
  extractPolygons?: ExtractRoomPolygonsFn,
): Promise<GeolocationStats> => {
  const polygonStatus = await apiService
    .get(`/assets/${assetId}/locations/polygon-status`)
    .catch(() => null);

  if (polygonStatus && polygonStatus.missingCount > 0 && extractPolygons) {
    try {
      const polygons = await extractPolygons();
      if (polygons && polygons.size > 0) {
        // Multi-model format: each entry keeps sourceFile + dbId for proper matching.
        const polygonData: Record<string, RoomPolygon> = {};
        polygons.forEach((polygon, key) => {
          polygonData[key] = {
            points: polygon.points,
            minZ: polygon.minZ,
            maxZ: polygon.maxZ,
            sourceFile: polygon.sourceFile,
            dbId: polygon.dbId,
          };
        });
        await apiService.post(`/assets/${assetId}/locations/cache-polygons`, { polygons: polygonData });
      }
    } catch (extractErr) {
      console.warn('[locationSystemActions] Polygon extraction failed, continuing with cached data:', extractErr);
    }
  }

  const result = await apiService.post(`/assets/${assetId}/pois/assign-by-geolocation`, {});
  return result?.stats || {};
};

// ── Location CRUD ────────────────────────────────────────────────────────────

/** Create a top-level Building location. */
export const createLocation = async (
  assetId: string,
  opts: { name: string; description?: string },
): Promise<any> =>
  apiService.post(`/assets/${assetId}/locations`, {
    name: opts.name.trim(),
    type: 'Building',
    parent_id: null,
    description: (opts.description || '').trim(),
    position_x: 0,
    position_y: 0,
    position_z: 0,
  });

/**
 * Create (or update, when `existingId` is given) a manual storey defined by its
 * bottom/top elevations. Mirrors LocationsPage.handleSaveManualStorey.
 */
export const saveManualStorey = async (
  assetId: string,
  opts: { name: string; bottom: number; top: number },
  existingId?: string,
): Promise<any> => {
  const payload = {
    name: opts.name.trim(),
    type: 'Building Storey',
    is_custom: true,
    parent_id: null,
    elevation: opts.bottom,
    height: opts.top - opts.bottom,
    position_x: 0,
    position_y: 0,
    position_z: opts.bottom,
  };
  return existingId
    ? apiService.put(`/assets/${assetId}/locations/${existingId}`, payload)
    : apiService.post(`/assets/${assetId}/locations`, payload);
};

/**
 * Merge ≥2 storeys into one spanning custom storey; optionally delete the originals.
 * Mirrors LocationsPage.handleSaveMergeStorey.
 */
export const mergeStoreys = async (
  assetId: string,
  opts: { name: string; storeys: LocationRow[]; destroyOrigins?: boolean },
): Promise<any> => {
  const bottom = Math.min(...opts.storeys.map(s => parseFloat(String(s.elevation || 0))));
  const top = Math.max(...opts.storeys.map(
    s => parseFloat(String(s.elevation || 0)) + parseFloat(String(s.height || 0)),
  ));

  const created = await apiService.post(`/assets/${assetId}/locations`, {
    name: opts.name.trim(),
    type: 'Building Storey',
    is_custom: true,
    parent_id: null,
    elevation: bottom,
    height: top - bottom,
    position_x: 0,
    position_y: 0,
    position_z: bottom,
  });

  if (opts.destroyOrigins) {
    for (const storey of opts.storeys) {
      await apiService.delete(`/assets/${assetId}/locations/${storey.id}`);
    }
  }
  return created;
};

/** Delete a single location (and its children, server-side). */
export const deleteLocation = async (assetId: string, locationId: string): Promise<any> =>
  apiService.delete(`/assets/${assetId}/locations/${locationId}`);

/** Delete EVERY location of an asset. Destructive — always confirm before calling. */
export const deleteAllLocations = async (assetId: string): Promise<any> =>
  apiService.delete(`/assets/${assetId}/locations`);

// ── Asset (POI) ⇄ location wiring ────────────────────────────────────────────

const locationPosition = (location: LocationRow) => ({
  x: location.position?.x ?? (location.position_x != null ? Number(location.position_x) : undefined),
  y: location.position?.y ?? (location.position_y != null ? Number(location.position_y) : undefined),
  z: location.position?.z ?? (location.position_z != null ? Number(location.position_z) : undefined),
});

/** Create a brand-new asset (POI) placed at, and assigned to, a location. */
export const createPOIInLocation = async (
  assetId: string,
  location: LocationRow,
  fields: { name: string; description?: string; type?: string; category?: string; icon?: string },
): Promise<any> => {
  const pos = locationPosition(location);
  const poiData = {
    title: fields.name.trim(),
    x: pos.x || 0,
    y: pos.y || 0,
    z: pos.z || 0,
    icon: fields.icon || '📍',
    poi_type_name: (fields.type || '').trim() || 'Generic',
    poi_group_name: fields.category || 'structure',
    visibility_check: true,
    importance: '5.00',
    custom_data: JSON.stringify({
      description: (fields.description || '').trim(),
      location: location.name,
      location_id: location.id,
      status: 'active',
      created_manually: true,
    }),
  };
  return apiService.post(`/assets/${assetId}/pois/batch`, { pois: [poiData] });
};

/** Assign an already-existing asset (POI) to a location. */
export const assignExistingPOIToLocation = async (
  assetId: string,
  poi: any,
  location: LocationRow,
): Promise<any> => {
  const pos = locationPosition(location);
  const customData = parseCustomData(poi) || {};
  const updatedPOIData = {
    ...poi,
    custom_data: JSON.stringify({
      ...customData,
      location: location.name,
      location_id: location.id,
    }),
    x: pos.x || parseFloat(poi.x) || 0,
    y: pos.y || parseFloat(poi.y) || 0,
    z: pos.z || parseFloat(poi.z) || 0,
  };
  return apiService.put(`/assets/${assetId}/pois/${poi.id}`, updatedPOIData);
};

/** Clear an asset's (POI's) location assignment. */
export const removePOIFromLocation = async (assetId: string, poi: any): Promise<any> => {
  const customData = parseCustomData(poi) || {};
  const updatedPOIData = {
    ...poi,
    custom_data: JSON.stringify({
      ...customData,
      location: '',
      location_id: undefined,
    }),
  };
  return apiService.put(`/assets/${assetId}/pois/${poi.id}`, updatedPOIData);
};

// ── Result formatting (shared wording for the compact in-pane result banner) ──

export const formatReassignResult = (result: ReassignResult): string => {
  const parts = [`Updated ${result.updated ?? 0}`, `unchanged ${result.unchanged ?? 0}`];
  if (result.failed) parts.push(`failed ${result.failed}`);
  return `${parts.join(', ')} — ${result.total ?? 0} total`;
};

export const formatGeolocationResult = (stats: GeolocationStats): string => {
  const parts = [`Assigned ${stats.assigned ?? 0}`];
  if (stats.multipleSpaces) parts.push(`multi-space ${stats.multipleSpaces}`);
  if (stats.storeyFallback) parts.push(`storey fallback ${stats.storeyFallback}`);
  if (stats.unassigned) parts.push(`unassigned ${stats.unassigned}`);
  return parts.join(', ');
};
